/**
 * Spec 10.2.1 §2.6 / D5 — THE shared request-post summary component.
 *
 * One source of "you are approving exactly this", rendered identically in the
 * approvals inbox row detail and (Phase 2) the composer request-auth step. It
 * renders the normalised AuthorisationSubjectSummary: a transaction subject
 * shows the accounts-touched table with projected running balances + overdraw
 * callout; an A2 subject shows its subject facts. The content hash is a
 * click-to-reveal chip, never printed inline. Expanding never mutates anything.
 */
import { HashChip } from "./HashChip";
import { Countdown } from "./Countdown";
import { money, requestedByLabel } from "./format";
import type { AuthorisationSubjectSummary } from "../../hooks/use-ledger-approvals";

interface SummaryCardProps {
  summary: AuthorisationSubjectSummary;
  contentHash: string;
  expiresAt?: string;
  requestedBy?: { kind: string; id: string; label: string | null };
}

function AlertMark() {
  return (
    <svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
      <path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    </svg>
  );
}

export function SummaryCard({ summary, contentHash, expiresAt, requestedBy }: SummaryCardProps) {
  const s = summary;
  const txn = s.txn_summary;
  const typeLabel = txn ? txn.type.charAt(0).toUpperCase() + txn.type.slice(1) : s.subject_kind.replace(/_/g, " ");
  const currency = s.currency ?? txn?.currency ?? "AUD";

  return (
    <div className={`summary-card${s.overdraw ? " summary-card--overdraw" : ""}`}>
      <div className="summary-card__head">
        <div className="grow">
          <div className="summary-card__type">{typeLabel}{txn?.instrument ? ` · ${String((txn.instrument as { kind?: string }).kind ?? "")}` : ""}</div>
          {s.amount_minor != null && (
            <div className="summary-card__amount"><span className="ccy">{currency}</span>{money(s.amount_minor)}</div>
          )}
          <div className="summary-card__reason">{s.gloss || s.title}</div>
        </div>
        <span className="pill pill--pending"><span className="dot" />awaiting authorisation</span>
      </div>

      <div className="summary-card__body">
        {txn ? (
          <table className="touch-table">
            <caption className="muted tiny">Accounts touched — balance after this posts</caption>
            <thead>
              <tr><th>Account</th><th className="num">Movement</th><th className="num">Projected balance</th></tr>
            </thead>
            <tbody>
              {txn.lines.map((l) => (
                <tr key={l.account_id}>
                  <td>{l.account_no} · {l.title_name}</td>
                  <td className="num"><span className="dir">{l.direction}</span> {money(l.amount_minor)}</td>
                  <td className={`num ${l.projected_balance_minor < 0 ? "projected--neg" : "projected"}`}>{money(l.projected_balance_minor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}

        {s.overdraw && txn ? (
          <div className="overdraw-note">
            <AlertMark />
            <div>
              <strong>This post takes an account negative.</strong>{" "}
              {txn.warnings.join(" ")} A recorded explanation is required before it can be authorised.
            </div>
          </div>
        ) : null}

        {!txn && s.subject_facts.length > 0 ? (
          <dl className="particulars">
            {s.subject_facts.map((f) => (
              <div key={f.k} style={{ display: "contents" }}>
                <dt>{f.k}</dt>
                <dd>{f.v}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        <dl className="particulars">
          {txn?.counterparty_name ? (<><dt>Counterparty</dt><dd>{txn.counterparty_name}</dd></>) : null}
          {txn?.effective_date ? (<><dt>Effective</dt><dd>{txn.effective_date}</dd></>) : null}
          <dt>Content hash</dt>
          <dd><HashChip hash={contentHash} /></dd>
        </dl>
      </div>

      {(expiresAt || requestedBy) && (
        <div className="summary-card__meta">
          {requestedBy ? (
            <>
              <span className={`avatar av-xs${requestedBy.kind === "agent" ? " avatar--agent" : ""}`}>
                {(requestedBy.label ?? requestedBy.kind).slice(0, 1).toUpperCase()}
              </span>
              <span>Prepared by <strong className="sec">{requestedByLabel(requestedBy)}</strong>{requestedBy.kind === "agent" ? " (agent)" : ""}</span>
            </>
          ) : null}
          <span className="spacer" />
          {expiresAt ? <Countdown expiresAt={expiresAt} /> : null}
        </div>
      )}
    </div>
  );
}
